import { useMediaQuery, useTheme } from '@mui/material';
import { ChevronDown, DollarSign, Wrench } from 'lucide-react';
import { useState } from 'react';

import { COLORS } from './constants';
import { PriceCredibilityStrip } from './PriceCredibilityStrip';
import { PriceWaterfallChart } from './PriceWaterfallChart';

import { Dialog } from '@/components/common/Dialog';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';

const TRADITIONAL_COMMISSION_RATE = 0.025;
const DEFAULT_HAUSER_FEE = 20000;

const fmtPrice = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const parseInputPrice = (s: string) => Number(s.replace(/[^0-9]/g, '')) || 0;

const PriceInput = ({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) => (
  <div style={{ flex: 1 }}>
    <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.accent2, marginBottom: 10 }}>
      {label}
    </div>
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <span style={{
        position: 'absolute', left: 14, fontSize: 15, fontWeight: 500,
        color: COLORS.accent2, pointerEvents: 'none',
      }}>
        $
      </span>
      <input
        className="ns-input"
        type="text"
        inputMode="numeric"
        value={value.toLocaleString('en-US')}
        onChange={(e) => onChange(parseInputPrice(e.target.value))}
        style={{
          width: '100%', background: '#333', border: 'none', borderRadius: 8,
          padding: '11px 14px 11px 28px', fontSize: 15, fontWeight: 500,
          color: COLORS.accent2, outline: 'none', textAlign: 'right',
        }}
      />
    </div>
  </div>
);

export interface NegotiationStrategyDialogProps {
  open: boolean;
  onClose: () => void;
  /** Real repair-cost range from the Top Concerns leverage calculation. */
  leverageMin: number;
  leverageMax: number;
}

export const NegotiationStrategyDialog = ({
  open, onClose, leverageMin, leverageMax,
}: NegotiationStrategyDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { propertyDetails } = useHauserEQResults(propertyId);
  // Prefer hauser-eq's synced listing price (propertyDetails) over the raw
  // Blueprint property record — same precedence used elsewhere (HomeownerHub).
  const listPrice = propertyDetails?.price ?? selectedProperty?.property.price ?? 0;

  // Advisor's independent estimate of true value isn't a stored field yet.
  // Default to zero spread (min = max = list price) rather than an assumed
  // range — any fabricated default would fake a market opinion nobody has
  // actually formed. The advisor must consciously widen this based on real
  // comps. Editable client-side only (not persisted) until there's a real
  // backend field for it.
  const [advisorMin, setAdvisorMin] = useState(() => Math.round(listPrice));
  const [advisorMax, setAdvisorMax] = useState(() => Math.round(listPrice));
  const [hauserFee, setHauserFee] = useState(DEFAULT_HAUSER_FEE);
  const [inputsOpen, setInputsOpen] = useState(true);

  // Ask for the top of the defensible range, not the midpoint — consistent
  // with anchoring the whole waterfall to the advisor's HIGH estimate, and
  // with how negotiation actually works: start from the ceiling.
  const repairCredit = Math.round(leverageMax);
  const traditionalCommission = Math.round(advisorMax * TRADITIONAL_COMMISSION_RATE);
  const netFeeSavings = Math.max(0, traditionalCommission - hauserFee);

  return (
    <Dialog
      open={open} onClose={onClose} width={760}
      containerProps={{ p: isMobile ? '10px' : '25px', gap: '16px' }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'rgba(187,183,175,0.1) !important',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          },
        },
        // MUI's own Backdrop sits directly behind the Paper and defaults to a
        // near-opaque dark scrim — the Paper's backdrop-filter only blurs
        // whatever's immediately behind it, which is that flat scrim, not the
        // real page content further back. Blurring a flat color shows nothing,
        // so the scrim has to be lightened for the glass effect to read.
        backdrop: {
          sx: {
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
          },
        },
      }}>
      <style>{`.ns-input:focus { box-shadow: 0 0 0 1.5px rgba(192,255,2,0.35); }`}</style>

      <div style={{
        background: COLORS.card, borderRadius: 16, padding: '20px',
        marginTop: isMobile ? 24 : 0,
      }}>
        <div
          onClick={() => setInputsOpen((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', marginBottom: inputsOpen ? 16 : 0,
          }}>
          <span style={{
            fontSize: 13, fontWeight: 600, letterSpacing: '0.3em',
            color: COLORS.accent2,
          }}>
            Advisor inputs
          </span>
          <ChevronDown
            size={14} color={COLORS.textDim}
            style={{ transform: inputsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          />
        </div>
        {inputsOpen && (
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
            <PriceInput label="Min. estimate" value={advisorMin} onChange={setAdvisorMin} />
            <PriceInput label="Max. estimate" value={advisorMax} onChange={setAdvisorMax} />
            <PriceInput label="Hauser fee" value={hauserFee} onChange={setHauserFee} />
          </div>
        )}
      </div>

      <PriceCredibilityStrip
        listPrice={listPrice}
        advisorMinPrice={advisorMin}
        advisorMaxPrice={advisorMax}
      />

      <PriceWaterfallChart
        title="Price negotiation potential"
        subtitle={
          "Anchored to your advisor's high estimate — not the list price, which may be " +
          'power-priced to drive up demand. Toggle a lever off to see how it changes ' +
          'your effective price and offer strategy.'
        }
        startLabel="Advisor's high estimate"
        startValue={advisorMax}
        referenceLines={[{ label: 'List price', value: listPrice }]}
        deductions={[
          {
            label: 'Repair credits (top concerns)', amount: repairCredit,
            icon: Wrench, color: COLORS.critical,
            strategyNote:
              `Your top concerns carry an estimated repair cost of ${fmtPrice(leverageMin)}–` +
              `${fmtPrice(leverageMax)}. Open by asking for the full ${fmtPrice(leverageMax)} as a ` +
              'seller credit at closing — expect the seller to negotiate it down, so starting at ' +
              'the ceiling protects your leverage rather than anchoring low from the outset. ' +
              'Credits are also often preferred over a price cut since the recorded sale price stays higher.',
          },
          {
            label: 'Hauser fee vs. traditional commission', amount: netFeeSavings,
            icon: DollarSign, color: 'var(--tx-lime-ink)', highlight: true,
            strategyNote:
              `A traditional buyer's agent would charge roughly ${fmtPrice(traditionalCommission)} ` +
              `(2.5% of ${fmtPrice(advisorMax)}). Hauser charges a flat ${fmtPrice(hauserFee)} ` +
              `instead — a net savings of ${fmtPrice(netFeeSavings)}, which grows on higher-priced homes.`,
          },
        ]}
      />
    </Dialog>
  );
};

export default NegotiationStrategyDialog;
